import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faChartLine,
  faCheckCircle,
  faClock,
  faCompass,
} from '@fortawesome/free-solid-svg-icons';
import MetricCard, { type MetricCardTrend } from './MetricCard';
import InsightsToggleGroup, { InsightsToggleOption } from './InsightsToggleGroup';
import { Statistics as StatisticsData } from '../api/jobApplications';
import { useNavigate } from 'react-router-dom';
import { InsightsMode } from '../types/insights';

const EMPTY_PIPELINE_ORDER: string[] = [];
const PIPELINE_ORDERS: Record<string, string[]> = {
  job_hunt: ['Prospect', 'Applied', 'Assessment', 'Interview', 'Offer', 'Rejected'],
  finance: ['Fraud Alert', 'Payment Due', 'Overdue', 'Payment Received', 'Refund', 'Tax Document', 'Account Update'],
  parenthood: ['Permission Required', 'Payment Required', 'Health', 'Enrollment', 'Report Card', 'Conference', 'Event Scheduled', 'Schedule Change'],
  business: ['Contract Pending', 'Approval Needed', 'Invoice Due', 'Proposal', 'Meeting Scheduled', 'Deadline Reminder', 'Project Update', 'Lead', 'Newsletter', 'Promotion', 'Info Update'],
};

const ENTITY_MODE_OPTIONS: ReadonlyArray<InsightsToggleOption<InsightsMode>> = [
  { id: 'operational', label: 'Operational', icon: '⚡' },
  { id: 'historical', label: 'Historical', icon: '🗂' },
] as const;

function buildWorkbenchEntityHref(currentDomain: string, entityName: string): string {
  const params = new URLSearchParams();

  params.set('view', currentDomain === 'job_hunt' ? 'applications' : 'all');

  // Life View aggregates entities across domains; Workbench defaults omitted `area` to the
  // user's active_domain_key, so we must pass area=all to match Insights and filter by entity only.
  if (currentDomain === 'all') {
    params.set('area', 'all');
  } else if (currentDomain !== 'job_hunt') {
    params.set('area', currentDomain);
  }

  params.set('entity', entityName);

  return `/app/workbench?${params.toString()}`;
}

function buildWeeklyCountTrend(delta: number): MetricCardTrend {
  if (delta === 0) {
    return {
      value: '0',
      isPositive: false,
      label: 'new this week',
      emphasizeDirection: false,
    };
  }

  return {
    value: delta > 0 ? `+${delta}` : `${delta}`,
    isPositive: delta > 0,
    label: 'new this week',
  };
}

interface StatisticsProps {
  stats: StatisticsData | null;
  currentDomain: string;
  currentMode: InsightsMode;
  onModeChange: (nextMode: InsightsMode) => void;
  entityLoading?: boolean;
  /** Prefer soft refresh via page cache instead of full reload. */
  onRefreshData: () => void;
}

const Statistics: React.FC<StatisticsProps> = ({
  stats,
  currentDomain,
  currentMode,
  onModeChange,
  entityLoading = false,
  onRefreshData,
}) => {
  const navigate = useNavigate();

  // Hooks must be unconditional; handle null stats inside.
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  useEffect(() => {
    setSelectedStatus(null);
  }, [currentDomain, currentMode]);

  // Build highlight set AND status-specific count map
  const { selectedHighlightSet, statusCountMap } = useMemo(() => {
    if (!selectedStatus || !stats?.status_entities) {
      return { selectedHighlightSet: new Set<string>(), statusCountMap: new Map<string, number>() };
    }
    const list = stats.status_entities[selectedStatus] || [];
    const highlightSet = new Set(list.map(e => (e.name || '').toLowerCase()));
    const countMap = new Map(list.map(e => [(e.name || '').toLowerCase(), e.count]));
    return { selectedHighlightSet: highlightSet, statusCountMap: countMap };
  }, [selectedStatus, stats?.status_entities]);


  // Domain-specific labels and icons
  const getDomainConfig = () => {
    switch (currentDomain) {
      case 'finance': return { label: 'Bills/Statements', icon: '💰', color: 'text-emerald-400', glow: 'shadow-emerald-500/20' };
      case 'job_hunt': return { label: 'Applications', icon: '💼', color: 'text-blue-400', glow: 'shadow-blue-500/20' };
      case 'parenthood': return { label: 'Forms/Activities', icon: '🧸', color: 'text-rose-400', glow: 'shadow-rose-500/20' };
      case 'business': return { label: 'Leads/Clients', icon: '🏢', color: 'text-amber-400', glow: 'shadow-amber-500/20' };
      default: return { label: 'Total Items', icon: '🌐', color: 'text-indigo-400', glow: 'shadow-indigo-500/20' };
    }
  };

  const domain = getDomainConfig();

  const currentPipelineOrder = useMemo(
    () => PIPELINE_ORDERS[currentDomain] || EMPTY_PIPELINE_ORDER,
    [currentDomain]
  );
  const hasDomainPipeline = currentPipelineOrder.length > 0;

  // Build breakdown from status_breakdown for domains with pipelines
  const breakdown = useMemo(() => {
    if (!stats) return {} as Record<string, number>;

    if (hasDomainPipeline) {
      const b: Record<string, number> = {};
      // 1. Add canonical stages (even if 0)
      currentPipelineOrder.forEach(status => {
        const count = (stats?.status_breakdown || {})[status] ?? 0;
        b[status] = count;
      });
      // 2. Add 'Triage' state if not already covered and has count
      const triageCount = (stats?.state_breakdown || {})['Triage'] ?? 0;
      if (triageCount > 0) {
        b['Triage'] = triageCount;
      }
      // 3. Add any other statuses that aren't in canonical order
      Object.entries(stats?.status_breakdown || {}).forEach(([status, count]) => {
        if (!currentPipelineOrder.includes(status) && status !== 'Triage') {
          b[status] = (b[status] || 0) + count;
        }
      });
      return b;
    }
    return (stats?.state_breakdown || stats?.status_breakdown || {});
  }, [stats, hasDomainPipeline, currentPipelineOrder]);

  const breakdownLabel = hasDomainPipeline ? 'Pipeline stages' : 'Lifecycle states';
  const breakdownTotal = Math.max(1, Object.values(breakdown).reduce((a, b) => a + b, 0));
  if (!stats) {
    return (
      <div className="text-center py-20 text-theme-muted font-medium tracking-widest uppercase text-xs">
        Insights data unavailable right now.
      </div>
    );
  }
  const hasWorkload = stats.total_items > 0;
  const totalChange = stats.total_change ?? 0;
  const activeChange = stats.active_change ?? 0;
  const entities = stats.entities ?? [];
  const domainDistribution = stats.domain_distribution ?? {};
  const totalChangeTrend =
    !hasWorkload
      ? undefined
      : buildWeeklyCountTrend(totalChange);
  const activeItemsTrend =
    !hasWorkload
      ? undefined
      : buildWeeklyCountTrend(activeChange);
  const entityModeLabel = currentMode === 'historical' ? 'historical' : 'operational';
  const entityStatusAnnounce = entityLoading || entities.length === 0 ? 'polite' : undefined;
  const entityLoadingDescription = currentMode === 'historical'
    ? 'Pulling in completed and archived work so the entity cloud reflects the full history for this lens.'
    : 'Focusing the entity cloud on open and triage work for the most actionable current posture.';
  const entityEmptyDescription = currentMode === 'historical'
    ? 'Completed and archived work still does not form an entity cluster in this context.'
    : 'Open and triage work in this context does not currently map to any entities.';

  const prettifyEntityName = (name: string) => {
    // If it's an email or has <email>, extract the clean part
    const emailMatch = name.match(/^(.*?)\s*<.*?>$/) || name.match(/^(.*?)@.*$/);
    if (emailMatch && emailMatch[1].trim()) {
      return emailMatch[1].trim();
    }
    return name;
  };

  const operationalRead = (() => {
    if (!hasWorkload) {
      return 'No items match this lens yet. Add work in Workbench or connect Gmail so Insights can show concentration and pipeline balance.';
    }
    const top = entities[0];
    const topName = top ? prettifyEntityName(top.name) : null;
    let msg = `${stats.total_items} tracked item${stats.total_items === 1 ? '' : 's'} in this lens.`;
    if (topName) {
      msg += ` Strongest concentration right now: ${topName} (${top.count})—use the entity hub to open Workbench with filters.`;
    } else {
      msg += ' Entity clusters will appear as organizations accumulate; use the stage list beside this hub to spot bottlenecks.';
    }
    if (stats.active_items > 10) {
      msg += ' Many items still need resolution—prioritize triage and next actions in Workbench.';
    } else if (stats.active_items > 0) {
      msg += ' Open workload is moderate; scan for stages that stay heavy too long.';
    } else {
      msg += ' Nothing has moved past triage yet—review the backlog and promote the next item into motion.';
    }
    return msg;
  })();

  const renderTopMetrics = () => {
    if (currentDomain === 'job_hunt') {
      const interviewDelta = stats.interview_change ?? 0;
      return (
        <>
          <MetricCard
            label="Applications in lens"
            value={stats.total_items}
            icon={faBriefcase}
            color="text-blue-400"
            helperText="Count of applications matching Job Search and the selected operational or historical mode."
            trend={totalChangeTrend}
          />
          <MetricCard
            label="In motion (excludes triage)"
            value={stats.active_items}
            icon={faClock}
            color="text-teal-400"
            helperText="Items past triage that are still open—your active pipeline load."
            trend={activeItemsTrend}
          />
          <MetricCard
            label="Interview share"
            value={!hasWorkload ? '—' : `${stats.interview_rate ?? 0}%`}
            icon={faCheckCircle}
            color="text-purple-400"
            helperText={
              hasWorkload
                ? 'Share of items currently in an interview-related status—shows emphasis on live conversations vs earlier stages.'
                : 'Appears once at least one application exists in this lens.'
            }
            trend={
              hasWorkload
                ? {
                  value: String(interviewDelta),
                  isPositive: interviewDelta > 0,
                  label: 'interviews this week',
                  emphasizeDirection: interviewDelta !== 0,
                }
                : undefined
            }
          />
          <MetricCard
            label="Typical close time"
            value={!hasWorkload ? '—' : stats.decision_velocity != null ? `${stats.decision_velocity}d` : '—'}
            icon={faChartLine}
            color="text-amber-400"
            helperText={
              hasWorkload
                ? 'Average days from first touch to done, using completed items—needs finished outcomes to read reliably.'
                : 'Average days from first touch to done once you complete applications in this lens.'
            }
            trend={
              hasWorkload && stats.decision_velocity != null
                ? {
                  value: `${stats.decision_velocity}`,
                  isPositive: false,
                  label: 'day avg',
                  emphasizeDirection: false,
                }
                : undefined
            }
          />
        </>
      );
    }

    const entityGroupsTrend =
      !hasWorkload
        ? undefined
        : entities.length > 0
          ? { value: 'Below', isPositive: false, label: 'entity hub', emphasizeDirection: false }
          : { value: 'None', isPositive: false, label: 'surfaced yet', emphasizeDirection: false };

    return (
      <>
        <MetricCard
          label={domain.label}
          value={stats.total_items}
          icon={faChartLine}
          color={domain.color}
          helperText="Baseline volume for the selected domain lens and operational or historical mode."
          trend={totalChangeTrend}
        />
        <MetricCard
          label="Active (excludes triage)"
          value={stats.active_items}
          icon={faClock}
          color="text-indigo-400"
          helperText="Open items that have moved past triage—signals how much is truly in flight."
          trend={activeItemsTrend}
        />
        <MetricCard
          label="Top entity groups"
          value={entities.length}
          icon={faBriefcase}
          color="text-teal-400"
          helperText="Distinct organizations in the entity cloud—concentration, not a task count."
          trend={entityGroupsTrend}
        />
        <MetricCard
          label="Gmail-linked share"
          value={!hasWorkload ? '—' : `${stats.ai_impact_score ?? 0}%`}
          icon={faCheckCircle}
          color="text-purple-400"
          helperText="Share of items with a linked Gmail message in this lens."
        />
      </>
    );
  };

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {stats.total_items === 0
          ? 'No items in this lens. Metric values use placeholders until work is captured.'
          : ''}
      </p>
      <section
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        aria-label="Summary metrics"
      >
        {renderTopMetrics()}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Status Breakdown Table */}
        <div className="lg:col-span-1 glass-card p-8 bg-theme-secondary border border-theme rounded-3xl overflow-hidden relative group shadow-sm">
          <div className="absolute top-0 right-0 p-6 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity text-theme">
            <FontAwesomeIcon icon={faChartLine} size="6x" />
          </div>
          <h2 className="text-sm font-black uppercase tracking-[0.2em] text-theme-secondary mb-2 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]"></span>
            {breakdownLabel}
          </h2>
          <p className="text-[11px] leading-relaxed text-theme-muted mb-6">
            {hasDomainPipeline
              ? 'Wider bars mean more items sitting in that stage—look for stages that stay heavy while others stay empty.'
              : 'Distribution of lifecycle states in this lens—use it to see whether work piles up in triage, active, or closed buckets.'}
          </p>
          <div className="space-y-4">
            {Object.entries(breakdown).length === 0 ? (
              <p className="text-theme-muted text-xs italic" role="status">
                No status breakdown for this lens yet—add items or broaden mode to populate stages.
              </p>
            ) : (
              Object.entries(breakdown)
                .sort((a, b) => {
                  // Preserve pipeline order for domains with defined pipelines
                  if (hasDomainPipeline) {
                    const idxA = currentPipelineOrder.indexOf(a[0]);
                    const idxB = currentPipelineOrder.indexOf(b[0]);

                    // Canonical stages first
                    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                    if (idxA !== -1) return -1;
                    if (idxB !== -1) return 1;

                    // Triage second to last
                    if (a[0] === 'Triage') return 1;
                    if (b[0] === 'Triage') return -1;
                  }
                  return b[1] - a[1];
                })
                .slice(0, 15) // Increased limit to accommodate all pipeline stages
                .map(([status, count]) => {
                  return (
                    <div key={status} className="space-y-2">
                      <div
                        className={`flex items-center justify-between group/row p-2 rounded-xl transition-colors ${hasDomainPipeline ? 'hover:bg-theme-card-hover cursor-pointer' : 'hover:bg-theme-card-hover'
                          }`}
                        role={hasDomainPipeline ? 'button' : undefined}
                        onClick={() => {
                          if (!hasDomainPipeline) return;
                          setSelectedStatus(prev => (prev === status ? null : status));
                        }}
                        title={hasDomainPipeline ? 'Click to highlight matching entities below' : undefined}
                      >
                        <span className="text-sm text-theme-secondary group-hover/row:text-theme transition-colors capitalize">
                          {status}
                        </span>
                        <div className="flex items-center gap-3">
                          <div className="h-1.5 w-24 bg-theme-secondary border border-theme rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500/40 rounded-full"
                              style={{ width: `${Math.min(100, (count / breakdownTotal) * 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-bold text-theme w-6 text-right">{count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        {/* Entity Hub - The "Constellation" */}
        <div
          className="lg:col-span-2 glass-card p-8 bg-theme-secondary border border-theme rounded-3xl relative overflow-hidden group shadow-sm"
          aria-busy={entityLoading}
          aria-live={entityStatusAnnounce}
          aria-atomic="true"
        >
          <div className="flex flex-col gap-4 mb-8 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-sm font-black uppercase tracking-[0.2em] text-theme-secondary flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.6)]"></span>
                Entity concentration
              </h2>
              <p className="mt-2 text-[10px] text-theme-muted font-medium uppercase tracking-widest leading-relaxed">
                {entityLoading
                  ? `Refreshing ${entityModeLabel} entity clusters`
                  : 'Where attention clusters in this lens—open Workbench with filters from a tag.'}
              </p>
            </div>
            <InsightsToggleGroup
              label="Insights entity mode"
              value={currentMode}
              options={ENTITY_MODE_OPTIONS}
              onChange={onModeChange}
              compact
              wrapperSx={{ pb: 0, flexShrink: 0 }}
            />
          </div>

          {/* When a stage is selected, show a helper above the hub */}
          {selectedStatus && !entityLoading && entities.length > 0 && (
            <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-theme-muted flex items-center gap-2">
              <span className="px-2 py-[2px] rounded-full bg-indigo-500/10 border border-indigo-400/40 text-indigo-500 font-bold">
                Highlighting {selectedStatus}
              </span>
              <button
                className="text-theme-muted hover:text-indigo-500 underline text-[10px] font-bold"
                onClick={() => setSelectedStatus(null)}
              >
                Clear
              </button>
            </div>
          )}

          {entityLoading ? (
            <div
              className="flex min-h-[220px] flex-col items-center justify-center rounded-3xl border border-theme px-6 text-center"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
              role="status"
            >
              <p className="text-theme-muted text-xs font-black uppercase tracking-[0.3em]">
                Loading {entityModeLabel} entities...
              </p>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-theme-secondary">
                {entityLoadingDescription}
              </p>
            </div>
          ) : entities.length === 0 ? (
            <div
              className="flex min-h-[220px] flex-col items-center justify-center rounded-3xl border border-theme px-6 text-center"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
              role="status"
            >
              <p className="text-theme-muted text-xs font-black uppercase tracking-[0.3em]">
                No entities in this mode
              </p>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-theme-secondary">
                {entityEmptyDescription}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2.5 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
              {entities.map((entity, idx) => {
                const entityKey = ((entity.name || '').toLowerCase() || `entity-${idx}`);
                const isHighlighted = selectedHighlightSet.has((entity.name || '').toLowerCase());
                return (
                  <button
                    key={entityKey}
                    onClick={() => navigate(buildWorkbenchEntityHref(currentDomain, entity.name))}
                    className={`group/entity relative px-4 py-2 rounded-2xl transition-all duration-300 flex items-center gap-3 overflow-hidden ${isHighlighted
                      ? 'bg-indigo-500/10 border border-indigo-500/40 hover:border-indigo-400/70'
                      : 'bg-theme-secondary border border-theme hover:border-indigo-500/30'
                      }`}
                    title={isHighlighted && selectedStatus ? `Has ${selectedStatus}-stage items` : undefined}
                  >
                    <div className={`absolute inset-0 transition-opacity ${isHighlighted
                      ? 'bg-gradient-to-br from-indigo-500/15 via-indigo-500/10 to-transparent opacity-100'
                      : 'bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover/entity:opacity-100'
                      }`}></div>
                    <div className="relative z-10 flex flex-col items-start">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-theme-secondary group-hover/entity:text-theme transition-colors">
                          {prettifyEntityName(entity.name)}
                        </span>
                        {isHighlighted && selectedStatus && (
                          <span className="text-[9px] uppercase tracking-tight px-2 py-[2px] rounded-full bg-amber-400/20 text-amber-600 border border-amber-300/40 font-black">
                            {selectedStatus}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 mt-0.5 opacity-80 group-hover/entity:opacity-100 transition-opacity">
                        {(entity.triage_count ?? 0) > 0 && (
                          <span className="text-[8px] uppercase tracking-tighter text-blue-500 font-black">{entity.triage_count ?? 0} In Triage</span>
                        )}
                        {(entity.active_count ?? 0) > 0 && (
                          <span className="text-[8px] uppercase tracking-tighter text-emerald-600 font-black">{entity.active_count ?? 0} Active</span>
                        )}
                      </div>
                    </div>
                    <span className={`h-5 min-w-[1.5rem] px-2 rounded-lg flex items-center justify-center text-[10px] font-bold relative z-10 ${isHighlighted
                      ? 'bg-amber-400/15 text-amber-600 border border-amber-300/40'
                      : 'bg-theme-secondary text-theme-muted group-hover/entity:text-theme-secondary border border-theme'
                      }`}>
                      {isHighlighted && selectedStatus
                        ? statusCountMap.get((entity.name || '').toLowerCase()) || entity.count
                        : entity.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {currentDomain === 'all' && Object.keys(domainDistribution).length > 0 && (
        <div className="glass-card p-8 bg-indigo-500/[0.02] border border-indigo-500/10 rounded-[3rem] text-center">
          <h2 className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-400/40 mb-2">Workload by domain</h2>
          <p className="text-[11px] text-theme-muted mb-8 max-w-xl mx-auto leading-relaxed">
            Item counts per domain in this lens—see which area owns the most volume before you drill into Workbench.
          </p>
          <div className="flex flex-wrap justify-center gap-12">
            {Object.entries(domainDistribution).map(([dom, count]) => (
              <div key={dom} className="flex flex-col items-center group">
                <div className="text-2xl mb-2 group-hover:scale-110 transition-transform duration-300">
                  {dom.toUpperCase() === 'JOB_HUNT' ? '💼' : dom.toUpperCase() === 'FINANCE' ? '💰' : dom.toUpperCase() === 'BUSINESS' ? '🏢' : dom.toUpperCase() === 'PARENTHOOD' ? '🧸' : '🌐'}
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-theme-secondary mb-1 group-hover:text-theme transition-colors">{dom.replace('_', ' ')}</p>
                <p className="text-xl font-bold text-theme">{count}</p>
                <div className="h-1 w-8 bg-indigo-500/20 rounded-full mt-2 group-hover:w-12 group-hover:bg-indigo-500/50 transition-all"></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-20 p-6 rounded-3xl bg-theme-secondary border border-theme flex flex-col gap-6 md:flex-row md:items-center md:justify-between group overflow-hidden relative shadow-sm">
        <div className="absolute inset-0 bg-indigo-500/[0.02] pointer-events-none" aria-hidden />
        <div className="flex items-start gap-6 relative z-10">
          <div className="h-12 w-12 shrink-0 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500 shadow-lg shadow-indigo-500/10 group-hover:bg-indigo-500/20 transition-all" aria-hidden>
            <FontAwesomeIcon icon={faCompass} className="text-xl" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-400/60 mb-1">Operational read</p>
            <p className="text-theme-secondary text-sm max-w-2xl leading-relaxed">
              {operationalRead}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefreshData}
          className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all relative z-10 shrink-0 self-start md:self-center"
          style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
        >
          Refresh data
        </button>
      </div>
    </div>
  );
};

export default Statistics;
